'use client';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { settlementApi } from '../../services/settlementApi';
import { vendorApi } from '../../services/vendorApi';
import { FileText, Plus, Filter } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700' },
    confirmed: { label: 'Onaylandı', color: 'bg-blue-100 text-blue-700' },
    paid: { label: 'Ödendi', color: 'bg-green-100 text-green-700' },
    disputed: { label: 'İtiraz', color: 'bg-red-100 text-red-700' },
};

export default function SettlementList() {
    const navigate = useNavigate();
    const [settlements, setSettlements] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState<any[]>([]);
    const [filters, setFilters] = useState({ vendorId: '', status: '' });

    // Generate modal state
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [generateForm, setGenerateForm] = useState({ vendorId: '', periodStart: '', periodEnd: '' });
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        fetchSettlements();
        fetchVendors();
    }, [filters]);

    const fetchVendors = async () => {
        try {
            const res = await vendorApi.getAllVendors({ limit: 100 });
            setVendors(res.vendors || []);
        } catch { /* ignore */ }
    };

    const fetchSettlements = async () => {
        try {
            setLoading(true);
            const params: any = { page: 1, limit: 50 };
            if (filters.vendorId) params.vendorId = filters.vendorId;
            if (filters.status) params.status = filters.status;
            const res = await settlementApi.getAll(params);
            setSettlements(res.settlements || []);
        } catch {
            toast.error('Mutabakatlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!generateForm.vendorId || !generateForm.periodStart || !generateForm.periodEnd) {
            toast.error('Tüm alanları doldurun');
            return;
        }
        try {
            setGenerating(true);
            await settlementApi.generate(generateForm);
            toast.success('Mutabakat oluşturuldu');
            setShowGenerateModal(false);
            setGenerateForm({ vendorId: '', periodStart: '', periodEnd: '' });
            fetchSettlements();
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Oluşturulamadı');
        } finally {
            setGenerating(false);
        }
    };

    const formatCurrency = (val: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);
    const formatDate = (val: any) => {
        if (!val) return '-';
        const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
        return d.toLocaleDateString('tr-TR');
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText size={24} /> Mutabakatlar
                </h1>
                <button
                    onClick={() => setShowGenerateModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> Yeni Mutabakat
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6 flex gap-4 items-end">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Vendor</label>
                    <select
                        value={filters.vendorId}
                        onChange={(e) => setFilters({ ...filters, vendorId: e.target.value })}
                        className="border rounded-lg px-3 py-2 text-sm"
                    >
                        <option value="">Tümü</option>
                        {vendors.map((v) => (
                            <option key={v.id} value={v.id}>{v.firstName} {v.lastName}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Durum</label>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="border rounded-lg px-3 py-2 text-sm"
                    >
                        <option value="">Tümü</option>
                        <option value="draft">Taslak</option>
                        <option value="confirmed">Onaylandı</option>
                        <option value="paid">Ödendi</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : settlements.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">Mutabakat bulunamadı</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 font-medium">
                            <tr>
                                <th className="px-4 py-3 text-left">Vendor</th>
                                <th className="px-4 py-3 text-left">Dönem</th>
                                <th className="px-4 py-3 text-left">Anlaşma</th>
                                <th className="px-4 py-3 text-right">Ciro</th>
                                <th className="px-4 py-3 text-right">Platform Payı</th>
                                <th className="px-4 py-3 text-right">Vendor Payı</th>
                                <th className="px-4 py-3 text-center">Sipariş</th>
                                <th className="px-4 py-3 text-center">Durum</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {settlements.map((s) => {
                                const st = STATUS_LABELS[s.status] || STATUS_LABELS.draft;
                                return (
                                    <tr
                                        key={s.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/admin/settlements/${s.id}`)}
                                    >
                                        <td className="px-4 py-3 font-medium">{s.vendorName}</td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {formatDate(s.periodStart)} - {formatDate(s.periodEnd)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                                {s.agreementType === 'profit_share' ? 'Kâr Ortaklığı' : 'Ciro Komisyonu'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">{formatCurrency(s.totalRevenue)}</td>
                                        <td className="px-4 py-3 text-right text-blue-600 font-medium">{formatCurrency(s.platformShare)}</td>
                                        <td className="px-4 py-3 text-right text-green-600 font-medium">{formatCurrency(s.vendorShare)}</td>
                                        <td className="px-4 py-3 text-center">{s.orderCount}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${st.color}`}>{st.label}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Generate Modal */}
            {showGenerateModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowGenerateModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-bold mb-4">Yeni Mutabakat Oluştur</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                                <select
                                    value={generateForm.vendorId}
                                    onChange={(e) => setGenerateForm({ ...generateForm, vendorId: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                >
                                    <option value="">Seçin...</option>
                                    {vendors.map((v) => (
                                        <option key={v.id} value={v.id}>{v.firstName} {v.lastName}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Dönem Başlangıcı</label>
                                    <input
                                        type="date"
                                        value={generateForm.periodStart}
                                        onChange={(e) => setGenerateForm({ ...generateForm, periodStart: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Dönem Bitişi</label>
                                    <input
                                        type="date"
                                        value={generateForm.periodEnd}
                                        onChange={(e) => setGenerateForm({ ...generateForm, periodEnd: e.target.value })}
                                        className="w-full border rounded-lg px-3 py-2"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={handleGenerate}
                                disabled={generating}
                                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                            >
                                {generating ? 'Oluşturuluyor...' : 'Oluştur'}
                            </button>
                            <button
                                onClick={() => setShowGenerateModal(false)}
                                className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
